import React from "react";
import { EditorState, convertFromRaw } from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import ViewEditor from "../../components/common/editor/ViewEditor.js";
import AttachmentButton from "../../components/common/AttachmentButton";
import { questionTypeConstants } from "../../helper/constants";
import { questionMCQAnswerTypeConstants } from "../../helper/constants";
import { AzureStorage } from "../../helper/azureStorage";

const PreviewQuestionView = ({
  question,
  index,
  questionContents,
  answerContents,
  allQuestions,    
  containerName
}) => {
  const questionType = question.qtype;
  const answerType = question.answer_type_id;

  // Parse main question content
  const questionRaw = JSON.parse(questionContents[index]);
  const questionState = EditorState.createWithContent(convertFromRaw(questionRaw[0]));

  // Parse model answer
  let modelAnswer = null;
  if (answerContents?.[index]) {
    try {
      const parsedAnswer = JSON.parse(answerContents[index]);
      if (parsedAnswer.length > 0) {
        modelAnswer = EditorState.createWithContent(convertFromRaw(parsedAnswer[0]));
      }
    } catch (err) {
      console.warn("Invalid model answer format", err);
    }
  }

  // Parse guidance
  let guidanceEditorState = null;
  try {
    if (question.guidence) {
      const g = JSON.parse(question.guidence);
      guidanceEditorState = EditorState.createWithContent(convertFromRaw(g[0]));
    }
  } catch {
    // ignore invalid guidance
  }

  // Handle MCQ Options
  const mcqOptions = questionType === questionTypeConstants.MCQ.id
    ? questionRaw.slice(1).map(raw => EditorState.createWithContent(convertFromRaw(raw)))
    : [];

  const getQuestionPassageContent = (content) => {
    let states = JSON.parse(content);
    let passageContentState = EditorState.createWithContent(convertFromRaw(states[0]));
    return passageContentState;
  }

  // Helper: Render Subquestions recursively
  const renderSubQuestions = () => {
    if (!question.sub_questions) return null;

    // sub_questions stored as CSV string of question IDs
    const subIds = question.sub_questions.split(",").map(id => id.trim());
    if (subIds.length === 0) return null;

    return (
      <div className="subquestions ml-4 mt-4 border-left pl-3">
        <h5>SubQuestions:</h5>
        {subIds.map((subIdStr, i) => {
          const subId = parseInt(subIdStr, 10);
          if (isNaN(subId)) return null;

          // Find subquestion object & index in allQuestions array
          const subQuestionIndex = allQuestions.findIndex(q => q.question_id === subId);
          if (subQuestionIndex === -1) return null;

          const subQuestion = allQuestions[subQuestionIndex];
          const subQuestionContentRaw = JSON.parse(questionContents[subQuestionIndex]);
          const subQuestionState = EditorState.createWithContent(convertFromRaw(subQuestionContentRaw[0]));

          let subModelAnswer = null;
          if (answerContents?.[subQuestionIndex]) {
            try {
              const parsedSubAnswer = JSON.parse(answerContents[subQuestionIndex]);
              if (parsedSubAnswer.length > 0) {
                subModelAnswer = EditorState.createWithContent(convertFromRaw(parsedSubAnswer[0]));
              }
            } catch {}
          }

          let subGuidanceState = null;
          try {
            if (subQuestion.guidence) {
              const g = JSON.parse(subQuestion.guidence);
              subGuidanceState = EditorState.createWithContent(convertFromRaw(g[0]));
            }
          } catch {}

          return (
            <div key={`subquestion-${subId}`} className="mb-4">
              <strong>{i + 1}. Question:</strong>
              <ViewEditor editorState={subQuestionState} />

              {(subQuestion.question_type === questionTypeConstants.MCQ.id && subQuestionContentRaw.length > 1) && (
                <div className="mt-2">
                  {subQuestionContentRaw.slice(1).map((raw, j) => (
                    <Editor
                      key={j}
                      editorState={EditorState.createWithContent(convertFromRaw(raw))}
                      toolbarHidden
                      readOnly
                      toolbarClassName="hide-toolbar"
                    />
                  ))}
                </div>
              )}

              {subModelAnswer && subQuestion.question_type !== questionTypeConstants.MCQ.id && (
                <div className="mt-2">
                  <strong>Model Answer:</strong>
                  <Editor
                    editorState={subModelAnswer}
                    toolbarHidden
                    readOnly
                    toolbarClassName="hide-toolbar"
                  />
                </div>
              )}

              <div className="mt-3">
                <strong>Marking Guidance:</strong>
                <div className="card p-2">
                    {subGuidanceState && subGuidanceState.getCurrentContent().hasText() ? (
                    <ViewEditor editorState={subGuidanceState} />
                    ) : (
                    <em>No marking guidance given.</em>
                    )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="preview-question-wrap mb-5">
      {question?.passage_content && (
        <div className="mb-3">
            <h5>Passage:</h5>
            <ViewEditor
                editorState={getQuestionPassageContent(question?.passage_content)}
            />
        </div>
      )}

      {/* ======= MAIN QUESTION ======= */}
      <div className="mb-3">
        {questionType === questionTypeConstants.SUBQUESTION.id ? <h5>Main Question:</h5> : <h5>Question:</h5>}
        <ViewEditor editorState={questionState} />
      </div>

      {/* ======= MCQ OPTIONS ======= */}
      {questionType === questionTypeConstants.MCQ.id && mcqOptions.length > 0 && (
        <div className="mt-3">
            <h5>Model Answer Options:</h5>
            {mcqOptions.map((optionState, idx) => {
            // 1. Get raw mark values from answerContents[index]
            let markVal = 0;
            try {
                const rawMark = answerContents?.[index];
                if (rawMark?.includes(',')) {
                    const splitMarks = rawMark.split(',').map(s => parseFloat(s.trim()));
                    markVal = splitMarks[idx] || 0;
                } else {
                    markVal = parseFloat(rawMark);
                }
            } catch (err) {
                markVal = 0;
            }

            const isCorrect = !isNaN(markVal) && markVal > 0;

            return (
                <div className={`row mb-2 d-flex align-items-center ${isCorrect ? "answersheet-correct" : ""}`} key={idx}>
                <div className="col-md-1 col-1">
                    {answerType === questionMCQAnswerTypeConstants.One.id ? (
                        <input type="radio" checked={isCorrect} disabled />
                        ) : (
                        <input type="checkbox" checked={isCorrect} disabled />
                    )}
                </div>
                <div className="col-md-11 col-11 answer-sheet-text">
                    <Editor
                        editorState={optionState}
                        toolbarHidden
                        readOnly
                        toolbarClassName="hide-toolbar"
                    />
                </div>
                </div>
            );
            })}
        </div>
      )}

      {/* ======= MODEL ANSWER for non-MCQ ======= */}
      {(questionType === questionTypeConstants.DESCRIPTIVE.id ||
        questionType === questionTypeConstants.SUBMISSION.id) && modelAnswer && (
        <div className="mt-3">
          <h5>Model Answer:</h5>
          <Editor
            editorState={modelAnswer}
            toolbarHidden
            readOnly
            toolbarClassName="hide-toolbar"
          />
        </div>
      )}

      {/* ======= SUBMISSION ATTACHMENTS ======= */}
      {questionType === questionTypeConstants.SUBMISSION.id && (
        <div className="mt-3">
          <h5>Model Answer Attachments:</h5>
          <div className="row">
            {getSubmissionAttachments(answerContents[index]).length > 0 ? (
              getSubmissionAttachments(answerContents[index]).map((file, i) => (
                <div className="col-md-4 col-6 mb-2" key={i}>
                  <AttachmentButton
                    btnName={file}
                    btnIconStyle="flaticon-download"
                    onClick={(e) => onDownloadAttachment(e, containerName, file)}
                  />
                </div>
              ))
              ) : (
                <div className="col-12 text-muted">
                  <em>No attachments provided.</em>
                </div>
              )
            }
          </div>
        </div>
      )}

      {/* ======= MARKING GUIDANCE ======= */}
      {questionType !== questionTypeConstants.SUBQUESTION.id && guidanceEditorState && (
        <div className="mt-3">
            <h5>Marking Guidance:</h5>
            <div className="card p-3">
            {guidanceEditorState.getCurrentContent().hasText() ? (
                <ViewEditor editorState={guidanceEditorState} />
            ) : (
                <em>No marking guidance given.</em>
            )}
            </div>
        </div>
       )}

      {/* ======= SUBQUESTIONS ======= */}
      {questionType === questionTypeConstants.SUBQUESTION.id && renderSubQuestions()}
    </div>
  );
};

export default PreviewQuestionView;

// Helper functions (same as before)
function getSubmissionAttachments(answerRaw) {
  if (!answerRaw) return [];
  return answerRaw
    .replace(/^\[(.+)\]$/, "$1")
    .split(",")
    .map(a => a.trim().replace(/"/g, ""))
    .filter(a => a && a !== "[]");
}

async function onDownloadAttachment(e, container, attach) {
    e.preventDefault()
    let fileReader
    async function handleFileRead() {
      const content = fileReader.result
    }
    fileReader = new FileReader()
    fileReader.onloadend = handleFileRead
    let content = await AzureStorage.downloadBlob(container, attach)
    fileReader.readAsArrayBuffer(content)
  }
